import { initializeTable } from '../tables';
import RowContextMenuButtonColumn from '../formatters/RowContextMenuButtonColumn';
import { getCookie } from '../../utils.js';
import { deletePrompt } from '../../prompts';
import { addAlert } from '../../alerts';

export default function initializeReportTable({ ...options }) {
    const tableElement = document.getElementById('reportsTable');
    const filtersElement = document.querySelector(
        '#reportsFilters .form-fields',
    );
    const tabsElement = document.querySelector('.table-tabs');

    if (!tableElement) {
        console.error('Reports table element not found');
        return;
    }

    const ajaxURL = tableElement.dataset.ajaxUrl;

    const table = initializeTable({
        tableElement: tableElement,
        filtersElement: filtersElement,
        tabsElement: tabsElement,
        ajaxURL: ajaxURL,
        pillsConfig: {
            containerSelector: '.active-filters-container',
            ignoreFields: ['is_deleted'],
        },
        columns: [
            {
                title: '#',
                formatter: 'rownum',
                headerSort: false,
                width: 60,
                resizable: false,
            },
            {
                title: gettext('Nazwa'),
                field: 'name',
            },
            {
                title: gettext('Status'),
                field: 'status',
                formatter: (cell) => {
                    const status = cell.getValue();
                    if (status === 'W trakcie') {
                        return `<span>${status} <span class="status-spinner" role="status" aria-label="loading"></span></span>`;
                    }
                    return status;
                },
            },
            {
                title: gettext('Utworzony przez'),
                field: 'created_by',
            },
            {
                title: gettext('Usunięty przez'),
                field: 'deleted_by',
                visible: false,
            },
            {
                title: gettext('Data zlecenia'),
                field: 'created_at',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: 'iso',
                    outputFormat: 'yyyy-MM-dd HH:mm',
                },
            },
            {
                title: gettext('Data ukończenia'),
                field: 'completed_at',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: 'iso',
                    outputFormat: 'yyyy-MM-dd HH:mm',
                },
            },
            {
                title: gettext('Data usunięcia'),
                field: 'deleted_at',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: 'iso',
                    outputFormat: 'yyyy-MM-dd HH:mm',
                },
                visible: false,
            },
            {
                title: gettext('Akcja'),
                resizable: false,
                headerSort: false,
                width: 100,
                formatter: RowContextMenuButtonColumn,
            },
        ],
        initialSort: [{ column: 'created_at', dir: 'desc' }],
        rowContextMenu: [
            {
                label: gettext('Szczegóły'),
                action: function (e, row) {
                    const rowData = row.getData();
                    window.location.href = `/reports/${rowData.id}/`;
                },
            },
            {
                label: gettext('Ponów generowanie'),
                action: async function (e, row) {
                    const rowData = row.getData();
                    try {
                        const response = await fetch(
                            `/api/reports/${rowData.id}/retry/`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-CSRFToken': getCookie('csrftoken'),
                                },
                            },
                        );

                        if (!response.ok) {
                            const error = await response.json();
                            if (window.alerts && window.alerts.addAlert) {
                                window.alerts.addAlert(
                                    'error',
                                    error.message ||
                                        'Błąd podczas ponownego generowania',
                                );
                            }
                            return;
                        }

                        const updatedReport = await response.json();
                        row.update(updatedReport);

                        if (window.alerts && window.alerts.addAlert) {
                            window.alerts.addAlert(
                                'success',
                                gettext(
                                    'Pomyślnie zlecono ponowne generowanie raportu.',
                                ),
                            );
                        }
                    } catch (error) {
                        console.error('Error retrying report:', error);
                        if (window.alerts && window.alerts.addAlert) {
                            window.alerts.addAlert(
                                'error',
                                error.message ||
                                    gettext('Wystąpił nieoczekiwany błąd.'),
                            );
                        }
                    }
                },
                disabled: function (row) {
                    const data = row.getData();
                    return data.status !== gettext('Niepowodzenie');
                },
            },
            {
                label: function (row) {
                    const data = row.getData();
                    return data.is_deleted
                        ? gettext('Usuń permanentnie')
                        : gettext('Usuń raport');
                },
                action: async function (e, row) {
                    const rowData = row.getData();
                    const isPermanent = rowData.is_deleted;
                    const url = isPermanent
                        ? `/api/reports/${rowData.id}/permanent/`
                        : `/api/reports/${rowData.id}/`;

                    const confirmMessage = isPermanent
                        ? gettext(
                              `Czy na pewno chcesz PERMANENTNIE usunąć raport "${rowData.name}"? TEJ OPERACJI NIE MOŻNA COFNĄĆ!`,
                          )
                        : gettext(
                              `Czy na pewno chcesz usunąć raport "${rowData.name}"?`,
                          );
                    deletePrompt({
                        options: {
                            title: this.label(row),
                            text: confirmMessage,
                        },
                        url: url,
                        successAction: function () {
                            row.delete();
                            addAlert(
                                'success',
                                gettext('Raport został pomyślnie usunięty.'),
                            );
                        },
                        failedAction: function (error) {
                            const message =
                                error?.data?.message ||
                                error?.message ||
                                gettext(
                                    'Wystąpił błąd podczas usuwania raportu.',
                                );
                            addAlert('error', message);
                        },
                    });
                },
            },
            {
                label: gettext('Pobierz Excel'),
                action: function (e, row) {
                    const url = row.getData().xlsx_file_url;
                    if (url) {
                        window.open(url, '_blank');
                    }
                },
                disabled: function (row) {
                    return !row.getData().xlsx_file_url;
                },
            },
        ],
        ...options,
    });

    const POLLING_INTERVAL_MS = 3000;
    const PENDING_STATUSES = [gettext('Oczekujący'), gettext('W trakcie')];
    let pollingInterval = null;

    const getPendingRows = () =>
        table
            .getRows()
            .filter((row) => PENDING_STATUSES.includes(row.getData().status));

    const checkForUpdates = async () => {
        const pendingRows = getPendingRows();
        if (pendingRows.length === 0) {
            clearInterval(pollingInterval);
            pollingInterval = null;
            return;
        }

        const ids = pendingRows.map((row) => row.getData().id);
        try {
            const response = await fetch('/api/reports/status/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify(ids),
            });
            if (!response.ok) {
                console.error(
                    'Failed to fetch report statuses:',
                    response.status,
                );
                return;
            }

            const updatedReports = await response.json();
            if (updatedReports.length > 0) {
                table.updateData(updatedReports);
            }

            if (getPendingRows().length === 0) {
                clearInterval(pollingInterval);
                pollingInterval = null;
            }
        } catch (error) {
            console.error('Error checking report updates:', error);
            clearInterval(pollingInterval);
            pollingInterval = null;
        }
    };

    const startPolling = () => {
        if (pollingInterval) return;
        if (getPendingRows().length > 0) {
            pollingInterval = setInterval(checkForUpdates, POLLING_INTERVAL_MS);
        }
    };

    table.on('ajaxResponseLoaded', () => setTimeout(startPolling, 0));

    window.addEventListener('beforeunload', () => {
        if (pollingInterval) clearInterval(pollingInterval);
    });

    return table;
}
